
import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface JournalEntryVisibilityCheckProps {
  clientId: string;
  children?: React.ReactNode;
  fallback?: React.ReactNode;
  onCheckComplete?: (hasPermission: boolean) => void;
}

const JournalEntryVisibilityCheck = ({
  clientId,
  children,
  fallback,
  onCheckComplete
}: JournalEntryVisibilityCheckProps) => {
  const [showEntries, setShowEntries] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function checkVisibilityPermission() {
      try {
        if (!clientId) {
          setShowEntries(false);
          if (onCheckComplete) onCheckComplete(false);
          setLoading(false);
          return;
        }
        
        const { data, error } = await supabase
          .from('user_profiles')
          .select('show_activity')
          .eq('user_id', clientId)
          .maybeSingle();
          
        if (error) {
          console.error("Error checking entry visibility:", error);
          setShowEntries(false);
          if (onCheckComplete) onCheckComplete(false);
        } else {
          // If show_activity is true, therapist can view entries
          const hasPermission = data?.show_activity === true;
          setShowEntries(hasPermission);
          if (onCheckComplete) onCheckComplete(hasPermission);
        }
      } catch (e) {
        console.error("Error in visibility check:", e);
        setShowEntries(false);
        if (onCheckComplete) onCheckComplete(false);
      } finally {
        setLoading(false);
      }
    }
    
    checkVisibilityPermission();
  }, [clientId, onCheckComplete]);
  
  if (loading) return null;
  
  if (showEntries === true) {
    return <>{children}</>;
  }
  
  return <>{fallback || null}</>;
};

export default JournalEntryVisibilityCheck;
